import React from 'react';
import { ScrollView, StyleSheet, View } from 'react-native';
import { Button, Modal, Portal, Text, TextInput } from 'react-native-paper';
import type { ReviewContactDetails } from '../types';
import { colors, spacing, typography } from '../constants/theme';

interface ReviewDetailsModalProps {
  visible: boolean;
  initialDetails: Partial<ReviewContactDetails>;
  loading?: boolean;
  showResend?: boolean;
  deliveryStatus?: string | null;
  onDismiss: () => void;
  onSave: (details: ReviewContactDetails) => Promise<void> | void;
  onResendRequested?: (details: ReviewContactDetails) => Promise<void> | void;
}

const cleanOptional = (value: string): string | null => value.trim() || null;
const isValidEmail = (value: string): boolean => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value);

export const ReviewDetailsModal: React.FC<ReviewDetailsModalProps> = ({
  visible,
  initialDetails,
  loading = false,
  showResend = false,
  deliveryStatus,
  onDismiss,
  onSave,
  onResendRequested,
}) => {
  const [memberName, setMemberName] = React.useState('');
  const [memberEmail, setMemberEmail] = React.useState('');
  const [homeManagerName, setHomeManagerName] = React.useState('');
  const [homeManagerEmail, setHomeManagerEmail] = React.useState('');
  const [error, setError] = React.useState<string | null>(null);

  React.useEffect(() => {
    if (!visible) return;
    setMemberName(initialDetails.memberName || '');
    setMemberEmail(initialDetails.memberEmail || '');
    setHomeManagerName(initialDetails.homeManagerName || '');
    setHomeManagerEmail(initialDetails.homeManagerEmail || '');
    setError(null);
  }, [
    visible,
    initialDetails.memberName,
    initialDetails.memberEmail,
    initialDetails.homeManagerName,
    initialDetails.homeManagerEmail,
  ]);

  const buildDetails = (): ReviewContactDetails | null => {
    const requiredName = memberName.trim();
    if (!requiredName) {
      setError('Member name is required.');
      return null;
    }

    const cleanedMemberEmail = cleanOptional(memberEmail);
    if (cleanedMemberEmail && !isValidEmail(cleanedMemberEmail)) {
      setError('Please enter a valid member email address.');
      return null;
    }

    const cleanedHomeManagerEmail = cleanOptional(homeManagerEmail);
    if (cleanedHomeManagerEmail && !isValidEmail(cleanedHomeManagerEmail)) {
      setError('Please enter a valid home manager email address.');
      return null;
    }

    setError(null);
    return {
      memberName: requiredName,
      memberEmail: cleanedMemberEmail,
      homeManagerName: cleanOptional(homeManagerName),
      homeManagerEmail: cleanedHomeManagerEmail,
    };
  };

  const handleSave = () => {
    const details = buildDetails();
    if (details) void onSave(details);
  };

  const handleResend = () => {
    const details = buildDetails();
    if (details) void onResendRequested?.(details);
  };

  return (
    <Portal>
      <Modal
        visible={visible}
        onDismiss={onDismiss}
        contentContainerStyle={styles.modal}
      >
        <ScrollView
          testID="review-details-modal"
          keyboardDismissMode="on-drag"
          keyboardShouldPersistTaps="handled"
        >
          <Text style={styles.title}>Review details</Text>
          <Text style={styles.helper}>
            Contact details appear on the report. Automatic delivery goes to the home manager and PURE.
          </Text>
          {showResend && deliveryStatus ? (
            <Text testID="review-report-delivery-status" style={styles.deliveryStatus}>
              Delivery status: {deliveryStatus}
            </Text>
          ) : null}
          <TextInput
            testID="review-details-member-name-input"
            label="Member name *"
            value={memberName}
            onChangeText={setMemberName}
            autoCapitalize="words"
            blurOnSubmit
            returnKeyType="done"
            style={styles.input}
          />
          <TextInput
            testID="review-details-member-email-input"
            label="Member email"
            value={memberEmail}
            onChangeText={setMemberEmail}
            keyboardType="email-address"
            autoCapitalize="none"
            blurOnSubmit
            returnKeyType="done"
            style={styles.input}
          />
          <TextInput
            testID="review-details-home-manager-name-input"
            label="Home manager name"
            value={homeManagerName}
            onChangeText={setHomeManagerName}
            autoCapitalize="words"
            blurOnSubmit
            returnKeyType="done"
            style={styles.input}
          />
          <TextInput
            testID="review-details-home-manager-email-input"
            label="Home manager email"
            value={homeManagerEmail}
            onChangeText={setHomeManagerEmail}
            keyboardType="email-address"
            autoCapitalize="none"
            blurOnSubmit
            returnKeyType="done"
            style={styles.input}
          />
          {error && <Text testID="review-details-error" style={styles.error}>{error}</Text>}
          <View style={styles.actions}>
            <Button mode="outlined" onPress={onDismiss} disabled={loading}>Cancel</Button>
            <Button
              testID="review-details-save-button"
              mode="contained"
              onPress={handleSave}
              loading={loading}
              disabled={loading}
            >
              Save details
            </Button>
          </View>
          {showResend && onResendRequested && (
            <Button
              testID="review-details-save-resend-button"
              mode="outlined"
              icon="email-send-outline"
              onPress={handleResend}
              disabled={loading}
              style={styles.resendButton}
            >
              Save and resend report
            </Button>
          )}
        </ScrollView>
      </Modal>
    </Portal>
  );
};

const styles = StyleSheet.create({
  modal: {
    backgroundColor: colors.background,
    margin: spacing.lg,
    padding: spacing.lg,
    borderRadius: 8,
    maxHeight: '90%',
  },
  title: {
    ...typography.h2,
    marginBottom: spacing.xs,
  },
  helper: {
    ...typography.body2,
    color: colors.textSecondary,
    marginBottom: spacing.md,
  },
  input: {
    marginBottom: spacing.sm,
    backgroundColor: colors.surface,
  },
  deliveryStatus: {
    ...typography.caption,
    color: colors.primary,
    marginBottom: spacing.md,
  },
  error: {
    color: colors.error,
    marginBottom: spacing.sm,
  },
  actions: {
    flexDirection: 'row',
    justifyContent: 'flex-end',
    gap: spacing.sm,
    marginTop: spacing.sm,
  },
  resendButton: {
    marginTop: spacing.md,
  },
});

export default ReviewDetailsModal;
